import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Snackbar, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import type { MapMouseEvent } from "mapbox-gl";
import { useWakeModelsWorker } from "../../api/dataReview";
import { useDataReview } from "../../contexts/DataReviewContext";
import { DrawingContext, DrawingMode } from "../../contexts/DrawingContext";
import { useMap } from "../../contexts/MapContext";
import { Layer } from "../../types/layer";
import { layoutToScreen, screenToLayout } from "../../utils/uiZoom";
import { BG, LINE, MUTED, TEAL, TEXT, btnPlain, btnPrimary, btnReject, btnSmall, floatSx, inputSx, menuItemSx, swatchSx } from "./editorTheme";
import { clearedWashVisible, fmtArea, metresPerPixel, PAINT_TERM } from "./editorModel";
import { useEditorData } from "./useEditorData";
import { useEditorActions, useEditorEffects } from "./useEditorActions";
import { EditorTool, useVegEditor } from "./VegEditorContext";
import { wandRegion } from "./wand";
import VegEditorHome from "./VegEditorHome";
import VegEditorSetup from "./VegEditorSetup";
import VegEditorCommandBar from "./VegEditorCommandBar";
import VegEditorRail from "./VegEditorRail";
import VegEditorDock from "./VegEditorDock";
import VegEditorSheet from "./VegEditorSheet";
import VegEditorMapFab from "./VegEditorMapFab";

const TOP = "calc(var(--topbar-h) + var(--strategies-h) + 12px)";
const PAINT_SWATCHES = 7;

/** The vegetation editor over the project map: its floating panels, the
 *  map tools (brush, wand, lasso, drawn outlines), keyboard shortcuts,
 *  toasts and the leave dialog. The map and its layers are the project
 *  page's own; this only adds what the editor needs on top. */
export default function VegEditor({ vegetationLayer }: { vegetationLayer: Layer | null }) {
  const d = useEditorData();
  const a = useEditorActions();
  const e = useVegEditor();
  const { currentId, setCurrentId, selectedIds, setSelectedIds, drawPurpose, showImagery, pickedImagery } = useDataReview();
  const { drawingMode, geojson } = useContext(DrawingContext);
  const { map } = useMap();
  const [search] = useSearchParams();
  const { phase, mode, tool, sheet, setup } = e;
  useEditorEffects();

  // Start the fine-detail model's machine as the editor opens: it takes a
  // few minutes, and is usually up by the time a run or a look-for needs it.
  const wakeWorker = useWakeModelsWorker(d.projectId);
  useEffect(() => { if (d.projectId) wakeWorker.mutate(); }, [d.projectId]);

  // ?resume lands straight in the refinement under way.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || !search.get("resume") || !d.resumable) return;
    resumed.current = true;
    a.openSession(d.resumable.id);
  }, [d.resumable?.id]);
  // A refinement that is gone (discarded, applied elsewhere) closes.
  useEffect(() => { if (phase === "session" && d.summary && e.sessionId && !d.job) a.goHome(); }, [phase, d.job, d.summary, e.sessionId]);
  // One target is always in hand once the run has found something.
  useEffect(() => {
    if (phase !== "session" || e.activeTerm) return;
    const keys = Object.keys(d.groups).filter((k) => k !== PAINT_TERM);
    if (keys.length) e.setActiveTerm(keys[keys.length - 1]);
  }, [phase, d.groups, e.activeTerm]);
  // And, while reviewing, one shape: the least sure undecided one.
  const firstPending = a.order.find((o) => o.status === "pending")?.id ?? null;
  useEffect(() => {
    if (phase !== "session" || mode !== "review" || !e.activeTerm || currentId !== null || selectedIds.length) return;
    if (firstPending !== null) a.select(firstPending, false);
  }, [phase, mode, e.activeTerm, firstPending, currentId, selectedIds.length]);
  // A shape that stopped existing (merged, carved away) leaves the card.
  useEffect(() => { if (d.currentGone) setCurrentId(null); }, [d.currentGone]);

  // The findings come for the map's view, padded a quarter each way; a pan
  // inside what was fetched, at about the same zoom, fetches nothing new.
  const fetched = useRef<{ bbox: [number, number, number, number]; zoom: number } | null>(null);
  useEffect(() => {
    if (!map) return;
    const update = () => {
      const b = map.getBounds();
      if (!b) return;
      const w = b.getWest(), s = b.getSouth(), east = b.getEast(), n = b.getNorth(), z = map.getZoom();
      const f = fetched.current;
      if (f && w >= f.bbox[0] && s >= f.bbox[1] && east <= f.bbox[2] && n <= f.bbox[3] && Math.abs(z - f.zoom) < 1) return;
      const pw = (east - w) * 0.25, ph = (n - s) * 0.25;
      const r = (v: number) => Math.round(v * 1e5) / 1e5;
      const next = { bbox: [r(w - pw), r(s - ph), r(east + pw), r(n + ph)] as [number, number, number, number], zoom: Math.round(z * 2) / 2 };
      fetched.current = next;
      e.setView(next);
    };
    update();
    map.on("moveend", update);
    return () => { map.off("moveend", update); };
  }, [map]);

  // Enter (or the tick on the map) finishes a drawn polygon, whatever it is for.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== "Enter" || drawingMode !== DrawingMode.DATA_AREA) return;
      const ring: number[][] | undefined = geojson?.features?.[0]?.geometry?.coordinates?.[0];
      if (!ring || ring.length < 3) return;
      const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring : [...ring, ring[0]];
      const polygon: GeoJSON.Polygon = { type: "Polygon", coordinates: [closed] };
      if (drawPurpose === "area") {
        e.patchSetup({ drawPoly: { type: "Feature", properties: {}, geometry: polygon } });
        a.stopDrawing();
      } else if (drawPurpose === "add" || drawPurpose === "subtract") {
        a.editOutline(drawPurpose, polygon);
        a.setTool(drawPurpose === "add" ? "draw" : "cut");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawingMode, geojson, drawPurpose, e.activeTerm, e.paintClass]);

  // Overlays drawn in screen space follow the map (see the effect below).
  const [view, setView] = useState(0);
  // Leaving the editor by any route (Back too) ends an outline it was drawing.
  const leaving = useRef({ drawingMode, stopDrawing: a.stopDrawing });
  leaving.current = { drawingMode, stopDrawing: a.stopDrawing };
  useEffect(() => () => { if (leaving.current.drawingMode === DrawingMode.DATA_AREA) leaving.current.stopDrawing(); }, []);

  // Wand clicks: on the current outline while editing, on the layer while
  // painting. The region is grown here in the browser from the photo
  // chunks the map already holds; only the polygon goes to the server. Off
  // the photo (or before it has loaded) the server's wand answers instead.
  const wandy = phase === "session" && ((mode === "paint" && tool === "wand") || (mode === "edit" && (tool === "wand" || tool === "unwand") && currentId !== null));
  const [wandPreview, setWandPreview] = useState<[number, number][] | null>(null);
  const wandSeq = useRef(0);
  const wandClick = useCallback(async (lng: number, lat: number) => {
    const seq = ++wandSeq.current;
    const overlays = d.job?.overlays ?? [];
    let region: GeoJSON.Polygon | null = null;
    try {
      region = overlays.length ? await wandRegion(overlays, lng, lat, e.tolerance) : null;
    } catch (err) {
      e.showToast((err as Error).message || "That picks up too much; lower the tolerance.");
      return;
    }
    if (seq !== wandSeq.current) return;
    if (!region) { a.wandAt(lng, lat); return; }
    // The outline stays up until the server has answered and the updated
    // shapes have come back, so there is no gap before the real polygon.
    const ring = region.coordinates[0] as [number, number][];
    setWandPreview(ring);
    const settled = (ok: boolean) => { if (ok) clearOnNextFeatures.current = true; else setWandPreview((p) => (p === ring ? null : p)); };
    // A paint fill is in the findings by the time it settles; an outline
    // edit arrives with the refetch that follows it.
    if (mode === "paint") a.paint({ geometry: region, target: e.paintClass }, () => setWandPreview((p) => (p === ring ? null : p)));
    else a.editOutline(tool === "unwand" ? "subtract" : "add", region, settled);
    window.setTimeout(() => setWandPreview((p) => (p === ring ? null : p)), 20_000);
  }, [d.job?.overlays, e, a, mode, tool]);
  const clearOnNextFeatures = useRef(false);
  useEffect(() => {
    if (clearOnNextFeatures.current) { clearOnNextFeatures.current = false; setWandPreview(null); }
  }, [d.features]);
  useEffect(() => {
    if (!map || !wandy) return;
    const onClick = (ev: MapMouseEvent) => { void wandClick(ev.lngLat.lng, ev.lngLat.lat); };
    map.on("click", onClick);
    map.getCanvas().style.cursor = "crosshair";
    return () => { map.off("click", onClick); map.getCanvas().style.cursor = ""; };
  }, [map, wandy, wandClick]);
  const wandPreviewPx = useMemo(() => {
    if (!map || !wandPreview) return null;
    const r = map.getContainer().getBoundingClientRect();
    const ox = screenToLayout(r.left), oy = screenToLayout(r.top);
    return wandPreview.map((p) => { const q = map.project(p); return `${ox + q.x},${oy + q.y}`; }).join(" ");
  }, [map, wandPreview, view]);

  // The brush: drag on the map paints (or erases) a stroke as wide as the
  // brush. Panning is off while it is in hand; Select pans.
  const brushing = phase === "session" && mode === "paint" && (tool === "brush" || tool === "revert");
  const [stroke, setStroke] = useState<[number, number][] | null>(null);
  // Strokes released but not yet saved: drawn until their own request
  // settles, whatever mode the editor is in by then.
  type SentStroke = { id: number; pts: [number, number][]; widthM: number; colour: string };
  const [sentStrokes, setSentStrokes] = useState<SentStroke[]>([]);
  const strokeSeq = useRef(0);
  const [ring, setRing] = useState<{ x: number; y: number } | null>(null);
  // Listeners are registered once per brush session. Everything they read
  // that changes between renders (the tool, the size, the class, the
  // actions) comes through refs, so a render during a drag does not swap
  // the listeners out and lose the stroke.
  const live = useRef({ tool, brushM: e.brushM, paintClass: e.paintClass, paint: a.paint, colour: TEAL });
  live.current = { tool, brushM: e.brushM, paintClass: e.paintClass, paint: a.paint, colour: tool === "revert" ? "#ffffff" : (d.colours[e.paintClass] ?? TEAL) };
  useEffect(() => {
    if (!map || !brushing) { setRing(null); setStroke(null); return; }
    const canvas = map.getCanvas();
    const container = map.getContainer();
    map.dragPan.disable(); map.doubleClickZoom.disable();
    canvas.style.cursor = "none";
    let pts: [number, number][] = [];
    let down = false;
    const toLngLat = (ev: MouseEvent) => {
      const r = container.getBoundingClientRect();
      const ll = map.unproject([screenToLayout(ev.clientX - r.left), screenToLayout(ev.clientY - r.top)]);
      return [ll.lng, ll.lat] as [number, number];
    };
    const onDown = (ev: MouseEvent) => { if (ev.button !== 0) return; down = true; pts = [toLngLat(ev)]; setStroke(pts); ev.preventDefault(); };
    const onMove = (ev: MouseEvent) => {
      // The map container reports its layout size from a screen-px corner
      // (Map.tsx), so its far edges are worked out in screen px here.
      const r = container.getBoundingClientRect();
      const right = r.left + layoutToScreen(r.width), bottom = r.top + layoutToScreen(r.height);
      const inside = ev.clientX >= r.left && ev.clientX <= right && ev.clientY >= r.top && ev.clientY <= bottom;
      setRing(inside ? { x: screenToLayout(ev.clientX), y: screenToLayout(ev.clientY) } : null);
      if (!down) return;
      pts = [...pts, toLngLat(ev)]; setStroke(pts);
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      const sent = pts;
      const line = sent.length > 1 ? sent : [sent[0], sent[0]];
      const { paint, brushM, paintClass, tool: t, colour } = live.current;
      const id = ++strokeSeq.current;
      setSentStrokes((list) => [...list, { id, pts: sent, widthM: brushM, colour }]);
      setStroke(null);
      // The saved shape is in the findings by the time this settles, so the
      // preview hands over without a gap; on failure it just goes.
      paint({ stroke: { type: "LineString", coordinates: line }, width_m: brushM, op: t === "revert" ? "erase" : "add", target: paintClass },
        () => setSentStrokes((list) => list.filter((x) => x.id !== id)));
    };
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      canvas.removeEventListener("mousedown", onDown); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp);
      map.dragPan.enable(); map.doubleClickZoom.enable(); canvas.style.cursor = "";
    };
  }, [map, brushing]);
  // The lassos: press, trace around, release. Select takes the shapes whose
  // centre is inside; fill paints the ground inside with the current class.
  const lassoing = phase === "session" && !sheet && ((mode === "review" && tool === "lasso") || (mode === "paint" && tool === "poly"));
  const [lasso, setLasso] = useState<[number, number][] | null>(null);
  const lassoLive = useRef({ mode, paintClass: e.paintClass, paint: a.paint, lassoSelect: a.lassoSelect });
  lassoLive.current = { mode, paintClass: e.paintClass, paint: a.paint, lassoSelect: a.lassoSelect };
  useEffect(() => {
    if (!map || !lassoing) { setLasso(null); return; }
    const canvas = map.getCanvas();
    const container = map.getContainer();
    map.dragPan.disable(); map.doubleClickZoom.disable();
    canvas.style.cursor = "crosshair";
    let pts: [number, number][] = [];
    let last: [number, number] | null = null;
    let down = false;
    const toLngLat = (ev: MouseEvent) => {
      const r = container.getBoundingClientRect();
      const ll = map.unproject([screenToLayout(ev.clientX - r.left), screenToLayout(ev.clientY - r.top)]);
      return [ll.lng, ll.lat] as [number, number];
    };
    const onDown = (ev: MouseEvent) => { if (ev.button !== 0) return; down = true; pts = [toLngLat(ev)]; last = [ev.clientX, ev.clientY]; setLasso(pts); ev.preventDefault(); };
    const onMove = (ev: MouseEvent) => {
      if (!down || !last) return;
      // One point every few screen pixels keeps the outline light.
      if (Math.hypot(ev.clientX - last[0], ev.clientY - last[1]) < 3) return;
      last = [ev.clientX, ev.clientY];
      pts = [...pts, toLngLat(ev)]; setLasso(pts);
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      const ring = pts;
      setLasso(null);
      if (ring.length < 3) return;
      const closed = [...ring, ring[0]];
      const polygon: GeoJSON.Polygon = { type: "Polygon", coordinates: [closed] };
      const { mode: m, paintClass, paint, lassoSelect } = lassoLive.current;
      if (m === "paint") { paint({ geometry: polygon, target: paintClass }); return; }
      void lassoSelect(polygon);
    };
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      canvas.removeEventListener("mousedown", onDown); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp);
      map.dragPan.enable(); map.doubleClickZoom.enable(); canvas.style.cursor = "";
    };
  }, [map, lassoing]);
  // Only while something is drawn over the map: re-rendering the whole
  // editor on every frame of every pan is what this used to cost.
  const following = brushing || !!lasso || !!wandPreview || sentStrokes.length > 0;
  useEffect(() => {
    if (!map || !following) return;
    const bump = () => setView((v) => v + 1);
    map.on("move", bump);
    return () => { map.off("move", bump); };
  }, [map, following]);
  const lassoPx = useMemo(() => {
    if (!map || !lasso) return null;
    const r = map.getContainer().getBoundingClientRect();
    const ox = screenToLayout(r.left), oy = screenToLayout(r.top);
    return lasso.map((p) => { const q = map.project(p); return `${ox + q.x},${oy + q.y}`; }).join(" ");
  }, [map, lasso, view]);
  const brushPx = useMemo(() => {
    if (!map) return 20;
    const c = map.getCenter();
    return Math.max(2, e.brushM / metresPerPixel(c.lat, map.getZoom()));
  }, [map, e.brushM, view]);
  // Released strokes waiting on the server, in screen space.
  const sentPx = useMemo(() => {
    if (!map || !sentStrokes.length) return [];
    const r = map.getContainer().getBoundingClientRect();
    const ox = screenToLayout(r.left), oy = screenToLayout(r.top);
    const mpp = metresPerPixel(map.getCenter().lat, map.getZoom());
    return sentStrokes.map((s) => ({
      id: s.id, colour: s.colour, width: Math.max(2, s.widthM / mpp),
      points: s.pts.map((p) => { const q = map.project(p); return `${ox + q.x},${oy + q.y}`; }).join(" "),
    }));
  }, [map, sentStrokes, view]);
  const strokePx = useMemo(() => {
    if (!map || !stroke) return null;
    const r = map.getContainer().getBoundingClientRect();
    const ox = screenToLayout(r.left), oy = screenToLayout(r.top);
    return stroke.map((p) => { const q = map.project(p); return [ox + q.x, oy + q.y] as [number, number]; });
  }, [map, stroke, view]);

  // Keyboard: the shortcuts the rail and the popover advertise.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      const k = ev.key, lk = k.toLowerCase();
      if (ev.metaKey || ev.ctrlKey || ev.altKey) {
        if ((ev.metaKey || ev.ctrlKey) && lk === "z" && mode === "edit") { ev.preventDefault(); a.undoOutline(); }
        if ((ev.metaKey || ev.ctrlKey) && lk === "z" && mode === "paint") { ev.preventDefault(); a.undoPaint(); }
        return;
      }
      if (k === "Escape") {
        if (e.confirmExit) return e.setConfirmExit(false);
        if (sheet) return e.setSheet(null);
        if (drawingMode === DrawingMode.DATA_AREA && phase === "setup") return a.stopDrawing();
        if (mode === "edit") return a.endEdit(false);
        if (mode === "paint") return a.cancelPaint();
        if (selectedIds.length) return setSelectedIds([]);
        return;
      }
      if (phase !== "session" || sheet) return;
      if (mode === "edit") {
        const m: Record<string, EditorTool> = { w: "wand", u: "unwand", d: "draw", c: "cut" };
        if (m[lk]) return a.setTool(m[lk]);
        if (k === "Enter" && drawingMode !== DrawingMode.DATA_AREA) return a.endEdit(true);
        return;
      }
      if (mode === "paint") {
        const m: Record<string, EditorTool> = { b: "brush", p: "poly", w: "wand", r: "revert" };
        if (m[lk]) return a.setTool(m[lk]);
        if (k === "[") return e.setBrushM(Math.max(1, e.brushM - 1));
        if (k === "]") return e.setBrushM(Math.min(40, e.brushM + 1));
        return;
      }
      if (lk === "a") { ev.preventDefault(); a.decide("accepted"); }
      else if (lk === "x" || k === "Delete" || k === "Backspace") { ev.preventDefault(); a.decide("rejected"); }
      else if (k === "ArrowRight" || lk === "j") { ev.preventDefault(); a.step(1); }
      else if (k === "ArrowLeft" || lk === "k") { ev.preventDefault(); a.step(-1); }
      else if (lk === "n") a.goNext();
      else if (lk === "e") a.beginEdit();
      else if (lk === "z") a.zoomTo(currentId);
      else if (lk === "l") a.setTool("lasso");
      else if (lk === "v") a.setTool("select");
      else if (lk === "b") a.toPaint();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, mode, sheet, drawingMode, selectedIds.length, e.confirmExit, e.brushM, a, currentId]);

  // ---- small pieces that live here -------------------------------------------
  const current = d.current;
  const paintClasses = useMemo(() => Array.from(new Set(["Lawn", "Tree", "Shrub", "Water", ...d.classes])), [d.classes]);
  const paintShown = paintClasses.slice(0, PAINT_SWATCHES);
  const paintRest = paintClasses.slice(PAINT_SWATCHES);
  const paintedM2 = d.groups[PAINT_TERM]?.acceptedM2 ?? 0;
  const topCentre = { position: "fixed" as const, top: TOP, left: "50%", transform: "translateX(-50%)", zIndex: 1100 };

  const legendLeft = phase === "setup" ? 396 : 12;
  const cleanView = !sheet && ((phase === "setup" && setup.startMode === "clean" && mode !== "paint") || (phase === "session" && d.isClean));
  const keepBld = phase === "setup" ? setup.keepBuildings : d.job?.options?.keep_buildings !== false;
  // "Not mapped yet" names the wash, so it is listed only while the wash shows.
  const washOn = clearedWashVisible({
    clean: cleanView, vegLayerOn: !!vegetationLayer?.active,
    photoOver: showImagery && (phase === "setup" ? !!pickedImagery : (d.job?.overlays?.length ?? 0) > 0),
  });
  const showMapLegend = cleanView && (washOn || keepBld);

  return (
    <>
      {phase === "home" && <VegEditorHome />}
      {phase === "setup" && <VegEditorSetup />}
      {phase === "setup" && setup.areaMode === "draw" && !setup.drawPoly && drawingMode === DrawingMode.DATA_AREA && (
        <Box sx={{ ...floatSx, ...topCentre, display: "flex", alignItems: "center", gap: 1, p: "8px 14px", color: "#9ff0d4", fontSize: 13 }}>
          Click around the area you want to refine, then press Enter or the tick
        </Box>
      )}
      {showMapLegend && (
        <Box sx={{ ...floatSx, position: "fixed", top: TOP, left: legendLeft, display: "flex", gap: 1.75, p: "7px 11px", fontSize: 11.5, zIndex: 1095 }}>
          {washOn && <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 10, height: 10, borderRadius: 0.75, border: "1px solid rgba(255,255,255,0.4)", bgcolor: "rgba(11,15,20,0.55)" }} /><span>Not mapped yet</span></Box>}
          {keepBld && <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={swatchSx("#c9a66b")} /><span>Building · locked</span></Box>}
        </Box>
      )}
      {phase === "session" && !sheet && mode === "review" && <VegEditorCommandBar />}
      {phase === "session" && !sheet && mode === "edit" && (
        <Box sx={{ ...floatSx, ...topCentre, display: "flex", alignItems: "center", gap: 1.5, p: "8px 8px 8px 14px", maxWidth: "calc(100% - 150px)", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13 }}><strong>Editing outline</strong> <span style={{ color: MUTED }}>· {current?.properties.label ?? ""} · {current ? fmtArea(current.properties.area_m2) : ""}</span></Typography>
          <Button sx={{ ...btnPlain, ...btnSmall }} onClick={a.undoOutline} disabled={!current?.properties.undo_depth}>Undo</Button>
          <Button sx={{ ...btnPlain, ...btnSmall }} onClick={() => a.endEdit(false)}>Cancel</Button>
          <Button sx={{ ...btnPrimary, ...btnSmall }} onClick={() => a.endEdit(true)}>Save &amp; accept</Button>
        </Box>
      )}
      {phase === "session" && !sheet && mode === "paint" && (
        <Box sx={{ ...floatSx, ...topCentre, p: 1, maxWidth: "calc(100% - 150px)", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            {paintShown.map((c) => {
              const on = e.paintClass === c;
              return (
                <Box key={c} component="button" onClick={() => e.setPaintClass(c)} title={c}
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, height: 30, px: 1.25, borderRadius: 1.75, cursor: "pointer", font: "inherit", fontSize: 12, whiteSpace: "nowrap", maxWidth: 190,
                    border: `1px solid ${on ? TEAL : "rgba(255,255,255,0.18)"}`, bgcolor: on ? "rgba(0,212,173,0.12)" : "rgba(255,255,255,0.05)", color: TEXT, "&:hover": { borderColor: on ? TEAL : "rgba(255,255,255,0.35)" } }}>
                  <Box sx={swatchSx(d.colours[c] ?? "#b35bd1")} />
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c}</Box>
                </Box>
              );
            })}
            {paintRest.length > 0 && (
              <Select size="small" displayEmpty value={paintRest.includes(e.paintClass) ? e.paintClass : ""} onChange={(ev) => ev.target.value && e.setPaintClass(String(ev.target.value))}
                sx={{ ...inputSx, height: 30, fontSize: 12, minWidth: 110 }} MenuProps={{ PaperProps: { sx: { bgcolor: "#1d2733", color: TEXT } } }}>
                <MenuItem value="" sx={menuItemSx}><em>More…</em></MenuItem>
                {paintRest.map((c) => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
              </Select>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Painting <strong style={{ color: TEXT }}>{e.paintClass}</strong></Typography>
            <Box sx={{ flex: 1 }} />
            {d.isClean && (d.jobSummary?.unmapped_m2 ?? 0) > 0.5 && !e.remainder?.mode && (
              <Button sx={{ ...btnPlain, ...btnSmall }} onClick={a.fillRest} title="Everything not mapped yet becomes this class when you apply">Fill what's left with {e.paintClass}</Button>
            )}
            <Typography sx={{ fontSize: 12, color: MUTED }}>{paintedM2 > 0 ? `${fmtArea(paintedM2)} painted` : "Nothing painted yet"}</Typography>
            <Button sx={{ ...btnPlain, ...btnSmall }} onClick={a.undoPaint} disabled={d.paintOps <= e.paintMark || a.busy.undoPaint} title="Take back the last stroke (⌘Z)">Undo</Button>
            <Button sx={{ ...btnPlain, ...btnSmall }} onClick={a.cancelPaint} disabled={a.busy.undoPaint} title="Take back everything painted since you opened Paint">Cancel</Button>
            <Button sx={{ ...btnPrimary, ...btnSmall }} onClick={a.toReview}>Done</Button>
          </Box>
        </Box>
      )}
      {phase === "session" && !sheet && <VegEditorRail />}
      {phase === "session" && !sheet && d.viewTruncated && (
        <Box sx={{ ...floatSx, position: "fixed", left: 12, bottom: 40, p: "7px 11px", fontSize: 12, color: MUTED, zIndex: 1090 }}>
          Too many shapes to draw at this zoom. Zoom in to see them all.
        </Box>
      )}
      {phase === "session" && !sheet && mode === "review" && <VegEditorDock />}
      {phase === "session" && sheet === "changes" && <VegEditorSheet />}
      <VegEditorMapFab vegetationLayer={vegetationLayer} />

      {brushing && ring && (
        <Box sx={{ position: "fixed", left: ring.x, top: ring.y, width: brushPx, height: brushPx, transform: "translate(-50%, -50%)", borderRadius: "50%", border: "1.5px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.55)", pointerEvents: "none", zIndex: 1200 }} />
      )}
      {lassoPx && (
        <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1199 }}>
          <polygon points={lassoPx} fill={TEAL} fillOpacity="0.12" stroke={TEAL} strokeWidth="1.75" strokeDasharray="5 4" strokeLinejoin="round" />
        </svg>
      )}
      {wandPreviewPx && (
        <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1199 }}>
          <polygon points={wandPreviewPx} fill={TEAL} fillOpacity="0.18" stroke={TEAL} strokeWidth="2" strokeLinejoin="round" />
        </svg>
      )}
      {sentPx.length > 0 && (
        <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1199 }}>
          {sentPx.map((s) => (
            <polyline key={s.id} points={s.points} fill="none" stroke={s.colour} strokeOpacity="0.6" strokeWidth={s.width} strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </svg>
      )}
      {brushing && strokePx && strokePx.length > 0 && (
        <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1199 }}>
          <polyline points={strokePx.map((p) => p.join(",")).join(" ")} fill="none" stroke={tool === "revert" ? "#ffffff" : (d.colours[e.paintClass] ?? TEAL)} strokeOpacity="0.6" strokeWidth={brushPx} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}

      <Snackbar open={!!e.toast} onClose={e.clearToast} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{ top: `calc(var(--topbar-h) + var(--strategies-h) + ${phase === "session" && mode === "review" ? 150 : 70}px) !important` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: 1.75, pr: 1, py: 1, borderRadius: 2.25, bgcolor: "#e9eef3", color: "#141a21", fontWeight: 500, fontSize: 13, boxShadow: "0 10px 30px rgba(0,0,0,0.4)", whiteSpace: "nowrap" }}>
          <span>{e.toast?.msg}</span>
          {e.toast?.undo && <Button sx={{ ...btnSmall, textTransform: "none", bgcolor: "#141a21", color: "#fff", "&:hover": { bgcolor: "#2a3642" } }} onClick={() => { e.toast?.undo?.(); e.clearToast(); }}>Undo</Button>}
        </Box>
      </Snackbar>
      <Snackbar open={!!e.error} autoHideDuration={6000} onClose={() => e.setError(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => e.setError(null)} sx={{ fontSize: 12.5 }}>{e.error}</Alert>
      </Snackbar>

      <Dialog open={e.confirmExit} onClose={() => e.setConfirmExit(false)} PaperProps={{ sx: { bgcolor: BG, color: TEXT, width: 460, border: `1px solid ${LINE}` } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Leave this refinement?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.55 }}>Your targets, decisions and edits are kept. Pick it up any time from the Vegetation layer on the map. Nothing reaches the fire model until you apply.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button sx={btnReject} onClick={a.discardSession}>Discard refinement</Button>
          <Box sx={{ flex: 1 }} />
          <Button sx={btnPlain} onClick={() => e.setConfirmExit(false)}>Stay</Button>
          <Button sx={btnPrimary} onClick={a.saveExit}>Save &amp; exit</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
